package br.com.vvrefeicoes.portal.core.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.Lucide

private val TriggerHeight = 48.dp
private const val DISABLED_ALPHA = 0.38f

@Composable
fun SearchableSelect(
    leadingIcon: ImageVector?,
    options: List<String>,
    selected: String,
    onSelect: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String? = null,
    searchPlaceholder: String = "Buscar",
    searchable: Boolean = false,
    showTriggerBorder: Boolean = true,
    maxMenuHeight: Dp = 260.dp,
    containerColor: Color = MaterialTheme.colorScheme.surface,
    contentColor: Color = MaterialTheme.colorScheme.onSurface,
    iconColor: Color = MaterialTheme.colorScheme.onSurfaceVariant,
    underlined: Boolean = false,
    borderColor: Color = MaterialTheme.colorScheme.outlineVariant,
    horizontalPadding: Dp = AppSpacing.space12,
    enabled: Boolean = true,
) {
    var expanded by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    val filtered =
        remember(options, query) {
            if (query.isBlank()) options else options.filter { it.contains(query, ignoreCase = true) }
        }
    val effectiveContentColor = if (enabled) contentColor else contentColor.copy(alpha = DISABLED_ALPHA)
    val effectiveIconColor = if (enabled) iconColor else iconColor.copy(alpha = DISABLED_ALPHA)
    val open =
        {
            if (enabled) {
                query = ""
                expanded = true
            }
        }

    Box(modifier = modifier) {
        if (underlined) {
            Column {
                Surface(
                    onClick = open,
                    enabled = enabled,
                    color = Color.Transparent,
                    contentColor = effectiveContentColor,
                    modifier = Modifier.fillMaxWidth().height(TriggerHeight),
                ) {
                    SelectTriggerRow(
                        leadingIcon = leadingIcon,
                        selected = selected,
                        placeholder = placeholder,
                        contentColor = effectiveContentColor,
                        iconColor = effectiveIconColor,
                        showChevron = enabled,
                        horizontalPadding = horizontalPadding,
                    )
                }
                if (enabled) {
                    Box(
                        modifier =
                            Modifier
                                .fillMaxWidth()
                                .height(1.dp)
                                .background(borderColor),
                    )
                }
            }
        } else {
            Surface(
                onClick = open,
                enabled = enabled,
                shape = AppShapes.medium,
                color = containerColor,
                contentColor = effectiveContentColor,
                border = if (searchable && showTriggerBorder) BorderStroke(1.dp, MaterialTheme.colorScheme.outline) else null,
                modifier = Modifier.fillMaxWidth().height(TriggerHeight),
            ) {
                SelectTriggerRow(
                    leadingIcon = leadingIcon,
                    selected = selected,
                    placeholder = placeholder,
                    contentColor = effectiveContentColor,
                    iconColor = effectiveIconColor,
                    showChevron = enabled,
                    horizontalPadding = horizontalPadding,
                )
            }
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = {
                expanded = false
                query = ""
            },
            modifier = Modifier.widthIn(min = 240.dp, max = 360.dp),
        ) {
            if (searchable) {
                val searchModifier =
                    Modifier
                        .fillMaxWidth()
                        .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space4)
                if (underlined) {
                    AppTextField(
                        value = query,
                        onValueChange = { query = it },
                        placeholder = searchPlaceholder,
                        singleLine = true,
                        modifier = searchModifier,
                    )
                } else {
                    OutlinedTextField(
                        value = query,
                        onValueChange = { query = it },
                        placeholder = { Text(searchPlaceholder) },
                        singleLine = true,
                        modifier = searchModifier,
                    )
                }
            }
            Column(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .heightIn(max = maxMenuHeight)
                        .verticalScroll(rememberScrollState()),
            ) {
                filtered.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option) },
                        onClick = {
                            onSelect(option)
                            expanded = false
                            query = ""
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun SelectTriggerRow(
    leadingIcon: ImageVector?,
    selected: String,
    placeholder: String?,
    contentColor: Color,
    iconColor: Color,
    showChevron: Boolean,
    horizontalPadding: Dp,
) {
    val hasValue = selected.isNotBlank()
    Row(
        modifier = Modifier.padding(horizontal = horizontalPadding),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        leadingIcon?.let { icon ->
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = iconColor,
            )
        }
        Text(
            text = if (hasValue) selected else placeholder.orEmpty(),
            style = MaterialTheme.typography.bodyMedium,
            color = if (hasValue || placeholder == null) contentColor else contentColor.copy(alpha = 0.6f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f),
        )
        if (showChevron) {
            Icon(Lucide.ChevronDown, contentDescription = null, tint = contentColor)
        }
    }
}
